/**
 * SDCX DK63 Machine 常规灯效（与公版驱动 UI 顺序一致）
 * 关闭灯光由灯光页 Switch 控制（mode=0），不在按钮组内
 */

/** 关闭灯光（Switch 关） */
export const SDCX_LIGHT_MODE_OFF = 0;

/** 常亮模式（仅亮度） */
export const SDCX_LIGHT_MODE_STATIC = 1;

/** 亮度档位 0~4 */
export const SDCX_LIGHTING_BRIGHTNESS_MIN = 0;
export const SDCX_LIGHTING_BRIGHTNESS_MAX = 4;

/** 速度档位 0~2 */
export const SDCX_LIGHTING_SPEED_MIN = 0;
export const SDCX_LIGHTING_SPEED_MAX = 2;

/** 公版 CMD 0x0A 回包有效区长度（Bus Hound） */
export const SDCX_LIGHT_PAYLOAD_SIZE = 10;

/** 灯光 payload 字段偏移（对齐公版 01 00 [mode] [brightness] [speed] …） */
export const SDCX_LIGHT_IDX_TYPE = 0;
export const SDCX_LIGHT_IDX_MODE = 2;
export const SDCX_LIGHT_IDX_BRIGHTNESS = 3;
export const SDCX_LIGHT_IDX_SPEED = 4;

export type SdcxLightEffectCaps = {
  brightness: boolean;
  speed: boolean;
};

export type SdcxUiLightEffect = {
  modeId: number;
  i18nKey: string;
  caps: SdcxLightEffectCaps;
};

/** 公版灯光页常规灯效（不含关闭） */
export const SDCX_UI_LIGHT_EFFECTS: readonly SdcxUiLightEffect[] = [
  {
    modeId: 1,
    i18nKey: "keyboard.lighting.sdcx.static",
    caps: { brightness: true, speed: false },
  },
  {
    modeId: 2,
    i18nKey: "keyboard.lighting.sdcx.breathing",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 3,
    i18nKey: "keyboard.lighting.sdcx.waveRight",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 4,
    i18nKey: "keyboard.lighting.sdcx.spread",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 5,
    i18nKey: "keyboard.lighting.sdcx.diagonalWave",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 6,
    i18nKey: "keyboard.lighting.sdcx.rippleOff",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 7,
    i18nKey: "keyboard.lighting.sdcx.chasing",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 8,
    i18nKey: "keyboard.lighting.sdcx.shrink",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 9,
    i18nKey: "keyboard.lighting.sdcx.marquee",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 10,
    i18nKey: "keyboard.lighting.sdcx.rowPressOff",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 11,
    i18nKey: "keyboard.lighting.sdcx.shuttle",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 12,
    i18nKey: "keyboard.lighting.sdcx.raindrops",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 13,
    i18nKey: "keyboard.lighting.sdcx.crossLight",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 14,
    i18nKey: "keyboard.lighting.sdcx.singleOn",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 15,
    i18nKey: "keyboard.lighting.sdcx.diagonalCut",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 16,
    i18nKey: "keyboard.lighting.sdcx.circles",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 17,
    i18nKey: "keyboard.lighting.sdcx.rippleOn",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 18,
    i18nKey: "keyboard.lighting.sdcx.slantedSweep",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 19,
    i18nKey: "keyboard.lighting.sdcx.singleOff",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 20,
    i18nKey: "keyboard.lighting.sdcx.waterTrace",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 21,
    i18nKey: "keyboard.lighting.sdcx.rowLight",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 22,
    i18nKey: "keyboard.lighting.sdcx.waveLeft",
    caps: { brightness: true, speed: true },
  },
  {
    modeId: 23,
    i18nKey: "keyboard.lighting.sdcx.centerCollision",
    caps: { brightness: true, speed: true },
  },
];

export function clampSdcxLightingLevel(value: number, max: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(max, Math.round(value)));
}

export function isSdcxLightOffMode(mode: number): boolean {
  return mode === SDCX_LIGHT_MODE_OFF;
}

export function getSdcxLightEffectCaps(mode: number): SdcxLightEffectCaps {
  const found = SDCX_UI_LIGHT_EFFECTS.find((item) => item.modeId === mode);
  return found?.caps ?? { brightness: true, speed: true };
}

export function normalizeSdcxRoutineLightMode(mode: number): number {
  if (isSdcxLightOffMode(mode)) {
    return SDCX_LIGHT_MODE_STATIC;
  }
  const matched = SDCX_UI_LIGHT_EFFECTS.find((item) => item.modeId === mode);
  return matched?.modeId ?? SDCX_LIGHT_MODE_STATIC;
}

export type SdcxLightingPayloadState = {
  mode: number;
  brightness: number;
  speed: number;
  lightOn: boolean;
};

/** 解析 CMD 0x0A 回包 DATA */
export function parseSdcxLightingPayload(
  payload: Uint8Array
): SdcxLightingPayloadState {
  const mode = payload[SDCX_LIGHT_IDX_MODE] ?? 0;
  return {
    mode,
    brightness: clampSdcxLightingLevel(
      payload[SDCX_LIGHT_IDX_BRIGHTNESS] ?? 0,
      SDCX_LIGHTING_BRIGHTNESS_MAX
    ),
    speed: clampSdcxLightingLevel(
      payload[SDCX_LIGHT_IDX_SPEED] ?? 0,
      SDCX_LIGHTING_SPEED_MAX
    ),
    lightOn: !isSdcxLightOffMode(mode),
  };
}

/** 合并 UI 变更并生成写入 payload（保留未知字节） */
export function buildSdcxLightingPayload(
  current: Uint8Array,
  patch: {
    mode?: number;
    brightness?: number;
    speed?: number;
    lightOn?: boolean;
  }
): Uint8Array {
  const next = new Uint8Array(
    Math.max(SDCX_LIGHT_PAYLOAD_SIZE, current.length)
  );
  next.set(current.slice(0, next.length));
  if ((next[SDCX_LIGHT_IDX_TYPE] ?? 0) === 0) {
    next[SDCX_LIGHT_IDX_TYPE] = 0x01;
  }

  if (typeof patch.brightness === "number") {
    next[SDCX_LIGHT_IDX_BRIGHTNESS] =
      clampSdcxLightingLevel(patch.brightness, SDCX_LIGHTING_BRIGHTNESS_MAX) &
      0xff;
  }
  if (typeof patch.speed === "number") {
    next[SDCX_LIGHT_IDX_SPEED] =
      clampSdcxLightingLevel(patch.speed, SDCX_LIGHTING_SPEED_MAX) & 0xff;
  }

  if (patch.lightOn === false) {
    next[SDCX_LIGHT_IDX_MODE] = SDCX_LIGHT_MODE_OFF;
  } else if (typeof patch.mode === "number") {
    const routineMode = normalizeSdcxRoutineLightMode(patch.mode);
    next[SDCX_LIGHT_IDX_MODE] = routineMode & 0xff;
  } else if (
    patch.lightOn === true &&
    isSdcxLightOffMode(next[SDCX_LIGHT_IDX_MODE] ?? 0)
  ) {
    next[SDCX_LIGHT_IDX_MODE] = SDCX_LIGHT_MODE_STATIC;
  }

  return next;
}

export function sdcxLightingLevelToUiPercent(
  level: number,
  max: number
): number {
  if (max <= 0) return 0;
  return Math.round((clampSdcxLightingLevel(level, max) / max) * 100);
}
